import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, takeUntil, withLatestFrom } from 'rxjs/operators';
import { selectInfo, selectRegistrationType, selectNraIndicator, selectAchInstructions } from '../../../account-info-header/reducers';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';
import { AmountType } from '../../../shared/types/Transaction';
import { ApiEndpointType, Constants, IRARegTypeCodes, transactionDetailLabels, transactionDirection } from '../../../shared/utilities/Constants';
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { mmddyyyyWithSlashes } from '../../../shared/utilities/regex-patterns';
import { AchActions, TransactionDetailsActions, TransactionFormActions } from '../../actions';
import { CashieringTransaction } from '../../models/overview/CashieringTransaction';
import { TaxWithholdingFields, TransactionDetails, TransactionForm, TransactionFormServiceError, isAchTransactionDetails } from '../../models/form/TransactionForm';
import {
  selectACHAvailableCashMMF,
  selectACHAvailableCashMargin,
  selectAchAmountType,
  selectAchDefaultProcessDate,
  selectAchInfoFrequencyList,
  selectAchInputFinished,
  selectAchStandingAuthId,
  selectAchTaxWithholding,
  selectCompletionDate,
  selectContributionOptions,
  selectFrequency,
  selectFundsAvailableToTrade,
  selectIsFromEdit,
  selectIsRecurring,
  selectMaxTransferAmount,
  selectOpenTwePopupConditions,
  selectSettlementDate,
  selectStandingAuthId,
  selectTransactionForm,
  selectTransactionFormDirection,
  selectTransactionFormServiceError,
  selectAchErrorList,
  selectTransactionFormTransactionDetails,
  selectVerifyCallFinished,
  selectWithdrawalDate,
  selectPayIncome,
  selectAchInfoDistributionReason,
  selectAchSelectedAuthorization
} from '../../reducers';
import { currencyStringToNumber } from '../../utilities/currencyStringToNumber';
import { buildFromPrefilledForm, resetGroup } from '../../utilities/formUtilities';
import { WarningOrError } from '../../models/shared/WarningOrError';
import { DistributionReason } from '../../models/internal-transfer/DistributionReason';

@Component({
  standalone: false,
  selector: 'mm-transaction-details-ach',
  templateUrl: './transaction-details-ach.component.html',
  styleUrls: ['./transaction-details-ach.component.scss']
})
export class TransactionDetailsAchComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Input() prefilledPrompts: TransactionDetails | undefined;
  @Input() isAddAchOnRequestProfile: boolean;
  @Input() isReviewEdit: boolean = false;
  @Input() isEdit: boolean = false;
  transactionDetailsGroup: UntypedFormGroup;
  datePickerFormControlName: string = 'completionDate';
  datePickerLabelName: string = Constants.completionDateLabel;
  expectedWithdrawalDateCopy: string = Constants.expectedWithdrawalDateCopy;
  withdrawalDateServiceError: string = Constants.withdrawalDateErrorMessage;
  fundsAvailableToTradeCopy: string = Constants.fundsAvailableToTrade;
  achIncomingIndicator: string = transactionDetailLabels.achIncomingIndicator;
  achOutgoingIndicator: string = transactionDetailLabels.achOutgoingIndicator;
  transactionDetails$: Observable<TransactionDetails | undefined>;
  maxTransferAmount$: Observable<string>;
  withdrawalDate$: Observable<string>;
  fundsAvailableToTrade$: Observable<string>;
  settlementDate$: Observable<string>;
  amountType$: Observable<AmountType>;
  contributionOptions$: Observable<string[]>;
  destroy$: Subject<boolean> = new Subject();
  isIra: boolean;
  isPtrs: boolean;
  transferDirection: string;
  maxDate: Date = new Date(new Date(3000, 11, 31).setHours(0, 0, 0, 0));
  readonly controlNames = {
    withdrawalDate: 'withdrawalDate',
    completionDate: 'completionDate',
    fundsAvailableToTrade: 'fundsAvailableToTrade',
    settlementDate: 'settlementDate',
    amount: 'amount',
    contributionReason: 'contributionReason',
    trustTaxCategory: 'trustTaxCategory',
    trustTaxCode: 'trustTaxCode'
  };
  inputCallFinished: boolean;
  transactionFormState: TransactionForm;
  hasWithdrawalDateServiceError: boolean;
  transactionFormServiceError$: Observable<TransactionFormServiceError>;
  hasServiceError: boolean = false;
  availableCashMMF: string;
  availableCashMargin: string;
  datePickerValidators: ValidatorFn[] = [
    Validators.required,
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate,
    CustomValidators.notGreaterThanMaxDate(this.maxDate)
  ];
  isCreateRecurringAchFromOnRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isRecurring$: Observable<boolean>;
  frequencyList$: Observable<any[]>;
  showEndDate: boolean;
  selectDateForMonth: boolean = false;
  showSecondCompletionDate: boolean;
  secondCompletionDateLabel: string = transactionDetailLabels.secondCompletionDate;
  secondCompletionDateValidators: ValidatorFn[] = [
    Validators.required,
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate
  ];
  endDateValidators: ValidatorFn[] = [
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate
  ];
  endDateLabel: string = Constants.endDateLabel;
  distributionReasonTooltip: string = '';
  isRecurring: any;
  regType: string;
  hasBadRequestServiceError: boolean;
  serviceErrorList: string[];
  defaultProcessDate: string;
  defaultContributionOption: string;
  isAchTweAuthorizationError: boolean;
  amountType: AmountType;
  errorBannerText: string;
  NonRothIraBanner: string;
  CashieringTransactions: CashieringTransaction;
  defaultAmountType: AmountType;
  taxWithholdings$: Observable<TaxWithholdingFields>;
  errorList: WarningOrError[];
  payIncome$: Observable<string | undefined>;
  distributionReason$: Observable<DistributionReason>;
  autoCalculateRmd$: Observable<boolean>;
  shouldDisableAmountType: boolean;
  achFundsSettlementBannerText: string = Constants.achFundsSettlementBannerText;

  constructor(private fb: UntypedFormBuilder, private store: Store, private datepipe: DatePipe, private ffs: FeatureFlagService) {
  }

  ngOnInit(): void {
    this.transactionDetailsGroup = this.fb.group({ [this.controlNames.withdrawalDate]: ['', Validators.required], });

    this.parentForm.setControl('transactionDetails', this.transactionDetailsGroup);

    this.store.select(selectInfo).pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.isIra = val.classifications.isIra;
        this.isPtrs = val.classifications.isPtrs;
      });

    this.store.select(selectTransactionFormDirection).pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.transferDirection = val;
        if (val === transactionDirection.in.toLowerCase()) {
          this.transactionDetailsGroup.setControl(this.controlNames.fundsAvailableToTrade, new FormControl('', Validators.required));
          this.transactionDetailsGroup.setControl(this.controlNames.settlementDate, new FormControl('', Validators.required));
        }
      });

    this.transactionDetails$ = this.store.pipe(
      select(selectTransactionFormTransactionDetails),
      map((details) => isAchTransactionDetails(details) ? details : undefined)
    );

    this.isRecurring$ = this.store.select(selectIsRecurring);
    this.frequencyList$ = this.store.select(selectAchInfoFrequencyList);
    this.distributionReason$ = this.store.select(selectAchInfoDistributionReason);

    this.store.select(selectRegistrationType).pipe(takeUntil(this.destroy$))
      .subscribe((iraRegtype) => {
        this.regType = iraRegtype;
      });

    this.payIncome$ = this.store.select(selectPayIncome);


    this.store.select(selectACHAvailableCashMMF).pipe(takeUntil(this.destroy$))
      .subscribe((info) => {
        this.availableCashMMF = info;
      });

    this.store.select(selectACHAvailableCashMargin).pipe(takeUntil(this.destroy$))
      .subscribe((info) => {
        this.availableCashMargin = info;
      });

    this.withdrawalDate$ = this.store.pipe(select(selectWithdrawalDate));
    this.withdrawalDate$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      // emitEvent set to false to avoid resetting withdrawalDdate & fundsAvailabletoTrade infinitely
      this.transactionDetailsGroup.controls.withdrawalDate?.setValue(val, { emitEvent: false });
    });

    this.fundsAvailableToTrade$ = this.store.pipe(select(selectFundsAvailableToTrade));
    this.fundsAvailableToTrade$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.transactionDetailsGroup.controls.fundsAvailableToTrade?.setValue(val, { emitEvent: false });
    });

    this.settlementDate$ = this.store.pipe(select(selectSettlementDate));
    this.settlementDate$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.transactionDetailsGroup.controls.settlementDate?.setValue(val, { emitEvent: false });
    });

    this.taxWithholdings$ = this.store.select(selectAchTaxWithholding);

    this.amountType$ = this.store.select(selectAchAmountType);
    this.amountType$.pipe(takeUntil(this.destroy$)).subscribe((amountType) => {
      this.amountType = this.defaultAmountType = amountType;
    });

    this.store.select(selectTransactionForm).pipe(takeUntil(this.destroy$))
      .subscribe((val) => this.transactionFormState = val);
    this.store.select(selectAchInputFinished).pipe(takeUntil(this.destroy$))
      .subscribe((callStatus) => this.inputCallFinished = callStatus);

    this.store.select(selectOpenTwePopupConditions).pipe(takeUntil(this.destroy$))
      .subscribe((popupConditions) => {
        const { verifyCallFinished, openTwePopup } = popupConditions;
        if (verifyCallFinished && !openTwePopup && !this.transactionFormState.hasError) {
          this.store.dispatch(TransactionFormActions.loadReviewPage());
        }
      });

    combineLatest([
      this.store.select(selectVerifyCallFinished),
      this.store.select(selectAchStandingAuthId),
      this.store.select(selectAchErrorList)
    ]).pipe(
      takeUntil(this.destroy$)
    )
      .subscribe(([verifyCallFinished, cashieringId, errorList]) => {
        if (verifyCallFinished && this.transactionFormState.hasError) {
          if (this.isAddAchOnRequestProfile && errorList.some((val) => val.messageId.toString() === Constants.minStateTaxErrorMessageCode)) {
            this.errorBannerText = Constants.minStateTaxWithholdingError;
          } else if (this.isAddAchOnRequestProfile && cashieringId === '0') {
            this.errorBannerText = Constants.onRequestAchProfileCreationFailed;
          } else {
            this.errorBannerText = this.transactionFormState.error;
          }
        }
      });

    this.contributionOptions$ = this.store.select(selectContributionOptions);

    this.maxTransferAmount$ = this.store.select(selectMaxTransferAmount);

    this.store.select(selectCompletionDate).pipe(takeUntil(this.destroy$))
      .subscribe((date) => {
        if (date && this.transactionDetailsGroup.controls.completionDate?.status === 'VALID') {
          const completionDate = this.datepipe.transform(date, 'yyyy-MM-dd') ?? '';
          this.store.dispatch(TransactionDetailsActions.loadWithdrawalDate({ completionDate: completionDate }));
        }
        this.transactionDetailsGroup.controls.withdrawalDate.setValue('', { emitEvent: false });
        if (this.isIncoming) {
          this.transactionDetailsGroup.controls.fundsAvailableToTrade.setValue('', { emitEvent: false });
        }
      });

    this.store
      .select(selectTransactionFormServiceError)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (val) =>
          this.hasWithdrawalDateServiceError
            = val.name === ApiEndpointType.withdrawalDate
      );

    this.transactionFormServiceError$ = this.store.select(selectTransactionFormServiceError);
    this.transactionFormServiceError$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      if (val.url.includes('MoneyLinkVerify')) {
        if (val.status.toString().startsWith('4') && val.errorList) {
          this.hasBadRequestServiceError = true;
          this.hasServiceError = false;
          this.serviceErrorList = val.errorList.map((err) => err.detail);
          this.isAchTweAuthorizationError = this.serviceErrorList.toString().startsWith(Constants.achCTWEShortenedResponseText) ? true : false;
        } else if (val.status.toString().startsWith('5')) {
          this.hasBadRequestServiceError = false;
          this.hasServiceError = false; //500s on verify should show up in transaction form, not details
        }
      } else if (val.status.toString().startsWith('5')) {
        this.hasServiceError = true;
      }
    });

    // RESET TRANSACTION DETAILS WHEN STANDING AUTH ID IS SWITCHED
    this.store.pipe(
      takeUntil(this.destroy$),
      select(selectStandingAuthId),
      distinctUntilChanged()
    ).subscribe((_) => this.resetDetails());

    // REBUILD FORM FROM EDIT FLOW
    this.store.select(selectIsFromEdit).pipe(
      takeUntil(this.destroy$),
    )
      .subscribe((isFromEdit) => {
        if (isFromEdit) {
          buildFromPrefilledForm(
            this.prefilledPrompts,
            this.transactionDetailsGroup,
            undefined
          );
          this.store.dispatch(TransactionFormActions.editLoadComplete());
        }
      });

    this.store.select(selectIsRecurring).pipe(takeUntil(this.destroy$))
      .subscribe((isRecurring) => {
        this.isCreateRecurringAchFromOnRequest$.next(isRecurring);
        this.distributionReasonTooltip = isRecurring ? Constants.wireAchDistributionReasonTooltip : '';
        this.isRecurring = isRecurring;
      });

    this.store.select(selectAchDefaultProcessDate).pipe(takeUntil(this.destroy$))
      .subscribe((processDate) => this.defaultProcessDate = processDate);

    this.store
      .select(selectFrequency)
      .pipe(
        takeUntil(this.destroy$),
        skip(1),
        filter((frequency) => frequency !== undefined),
        distinctUntilChanged(),
        withLatestFrom(this.contributionOptions$, this.store.select(selectAchDefaultProcessDate))
      )
      .subscribe(([frequency, options, defaultProcessDate]) => {
        this.defaultContributionOption = '';
        if (options.length === 1) {
          this.defaultContributionOption = this.transactionDetailsFormGroup.controls?.contributionReason?.value;
        }
        this.transactionDetailsFormGroup?.reset();
        this.transactionDetailsFormGroup?.controls.contributionReason?.setValue(this.defaultContributionOption);
        this.transactionDetailsGroup?.controls.completionDate?.setValue(defaultProcessDate);
        this.transactionDetailsFormGroup?.controls.amountType?.setValue(this.defaultAmountType);
        this.store.dispatch(AchActions.resetTaxWithholdingsToDefault());
      });

    this.transactionDetailsGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((form) => {
      if (form.completionDate) {
        this.transactionDetailsGroup.controls.completionDate?.addValidators(CustomValidators.notLessThanMinDate(new Date(this.defaultProcessDate)));
        this.showBannerForNonRothIRA(form.completionDate);
      }
    });

    combineLatest([
      this.isRecurring$,
      this.store.select(selectAchInstructions),
      this.store.select(selectAchSelectedAuthorization),
      this.store.select(selectNraIndicator)
    ]).pipe(
      takeUntil(this.destroy$),
    )
      .subscribe(([isRecurring, achInstructions, selectedAchProfile, nraIndicator]) => {
        const selectedAuthorization = selectedAchProfile?.split('-')[1];
        achInstructions.forEach((instruction) => {
          if (instruction.cashieringRqstId === selectedAuthorization) {
            const hasFrequency = instruction.frequencyDescription != null && instruction.frequencyDescription != undefined;
            this.shouldDisableAmountType = nraIndicator
              || (this.isOutgoing && !(instruction.isRecurring || hasFrequency) && !isRecurring); //is outgoing, on-request profile, one-time freq
          }
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    this.transactionDetailsGroup.removeControl(this.controlNames.contributionReason);
  }

  get transactionDetailsFormGroup() {
    return this.parentForm.controls?.transactionDetails as UntypedFormGroup;
  }

  get taxWithholdingsFormGroupDetails() {
    if (isAchTransactionDetails(this.prefilledPrompts)) {
      return this.prefilledPrompts?.taxWithholdingsFormGroup;
    }
  }

  get showContributionReason(): boolean {
    return this.isIra && this.isIncoming;
  }

  get IRARegTypeCodes(): boolean {
    return IRARegTypeCodes.includes(this.regType);
  }

  showBannerForNonRothIRA(form: any) {
    this.selectDateForMonth = false;
    if (new Date(form).getDate() <= 4) {
      if (this.isRecurring && !this.isIncoming) {
        this.selectDateForMonth = true;
        this.NonRothIraBanner = Constants.recurringAchNonRothIRA;
      } else if (new Date(form).getMonth() === 0 && !this.isIncoming) {
        this.selectDateForMonth = true;
        this.NonRothIraBanner = Constants.onetimeAchNonRothIRA;
      }
    }
  }

  get isIncoming(): boolean {
    return this.transferDirection === transactionDirection.in.toLowerCase();
  }

  get isOutgoing(): boolean {
    return this.transferDirection === transactionDirection.out.toLowerCase();
  }

  get availableCash(): number {
    return currencyStringToNumber(this.availableCashMargin) + currencyStringToNumber(this.availableCashMMF);
  }

  // We do +2 here because the completion date is always at least +1 (tomorrow)
  get overdraftRulesFutureDate(): Date {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    return new Date(today.setDate(today.getDate() + 2));
  }

  /* TODO: revisit reset when we upgrade to an angular version that supports FormControlOptions
     which contains a flag 'initialValueIsDefault' that will allow us to just use FormControl/FormGroup.reset()
  */
  resetDetails(): void {
    resetGroup(this.transactionDetailsGroup);
  }

  get displayTaxwithholdingsFields() {
    return this.isIra && !this.isIncoming && this.inputCallFinished;
  }

  get minDate(): Date {
    return new Date(this.defaultProcessDate);
  }

  get today(): Date {
    return new Date();
  }

  onFrequencyChanged(frequency: string): void {
    if (frequency) {
      this.showEndDate = true;
      if (frequency === 'SEMIMTH') {
        this.showSecondCompletionDate = true;
      } else {
        this.showSecondCompletionDate = false;
      }
    } else {
      this.showEndDate = false;
      this.showSecondCompletionDate = false;
    }
  }
}
